import { useState, useEffect, useRef, useCallback } from "react";
import type { Booking } from "@scheduler/shared";
import { formatTime } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";

interface UpcomingCardProps {
  bookings: Booking[];
  currentTime: Date;
}

const AUTO_COLLAPSE_MS = 60 * 1000; // 1 minute

export function UpcomingCard({ bookings, currentTime }: UpcomingCardProps) {
  const [expanded, setExpanded] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const now = currentTime.getTime();

  const upcoming = bookings
    .filter((b) => new Date(b.start_time).getTime() > now)
    .sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
    );

  const hasMore = upcoming.length > 1;

  // Reset the auto-collapse timer on any interaction
  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setExpanded(false), AUTO_COLLAPSE_MS);
  }, []);

  // Start/clear timer when expanded state changes
  useEffect(() => {
    if (expanded) {
      resetTimer();
    } else if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [expanded, resetTimer]);

  const handleExpand = () => {
    setExpanded(true);
    resetTimer();
  };

  const handleCollapse = () => {
    setExpanded(false);
  };

  // Reset timer on scroll interaction inside the list
  const handleScroll = () => {
    if (expanded) resetTimer();
  };

  return (
    <div className="absolute top-14 right-5 z-20 w-[200px]">
      <div
        className="rounded-xl overflow-hidden transition-all duration-300"
        style={{
          background: "rgba(0, 0, 0, 0.35)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-[10px] font-medium text-white/35 uppercase tracking-widest">
            Upcoming
          </span>
          {hasMore && !expanded && (
            <span className="text-[10px] text-white/25">{upcoming.length}</span>
          )}
        </div>

        {/* Content */}
        <div className="px-3 pb-2.5">
          {upcoming.length === 0 ? (
            <p className="text-xs text-white/25">No upcoming meetings</p>
          ) : expanded ? (
            <div
              className="max-h-[220px] overflow-y-auto pr-1 -mr-1"
              onScroll={handleScroll}
            >
              {upcoming.map((booking) => (
                <MeetingRow key={booking.id} booking={booking} />
              ))}
            </div>
          ) : (
            <MeetingRow booking={upcoming[0]} />
          )}
        </div>

        {/* Footer button — expand or collapse */}
        {hasMore && (
          <button
            onClick={expanded ? handleCollapse : handleExpand}
            className="w-full flex items-center justify-center gap-1 py-2 border-t border-white/[0.06] text-white/25 hover:text-white/40 transition-colors"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3 w-3" />
                <span className="text-[10px] font-medium">Collapse</span>
              </>
            ) : (
              <>
                <ChevronDown className="h-3 w-3" />
                <span className="text-[10px] font-medium">Show all</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function MeetingRow({ booking }: { booking: Booking }) {
  const startStr = formatTime(new Date(booking.start_time).toISOString());
  const endStr = formatTime(new Date(booking.end_time).toISOString());

  return (
    <div className="py-1.5 border-t border-white/[0.06] first:border-t-0">
      <p className="text-xs font-medium text-white/60 truncate">
        {booking.title}
      </p>
      <p className="text-[10px] text-white/25 truncate mt-0.5">
        {booking.organizer_name}
      </p>
      <p className="text-[10px] text-white/20 tabular-nums mt-0.5">
        {startStr} – {endStr}
      </p>
    </div>
  );
}
